import { useId } from 'react';
import { formatFuzzyDate, monthNames } from '../domain/dates';
import type { DateKind, FuzzyDate, PersonWithNames, UUID } from '../domain/types';
import { useI18n } from '../i18n';
import { displayName } from '../domain/names';

const KINDS: DateKind[] = ['exact', 'month', 'year', 'about', 'decade', 'range', 'relative', 'before_after', 'unknown'];

/**
 * Date entry that keeps uncertainty (section 11.3). An approximate answer is
 * never silently turned into an exact date — the preview line always shows
 * exactly how it will be displayed.
 */
export function FuzzyDateInput({
  value,
  onChange,
  people = [],
  events = [],
  kinds = KINDS,
  label,
  compact,
  defaultToToday,
}: {
  value: FuzzyDate;
  onChange: (d: FuzzyDate) => void;
  people?: PersonWithNames[];
  events?: Array<{ id: UUID; title: string; titleHe?: string }>;
  kinds?: DateKind[];
  label?: string;
  compact?: boolean;
  /** When switching to exact / month / year, start from today (photos, events) rather than a guess in the past (births). */
  defaultToToday?: boolean;
}) {
  const { t, lang } = useI18n();
  const id = useId();
  const thisYear = new Date().getFullYear();

  const parts = parseParts(value.start);
  const endParts = parseParts(value.end);

  const setKind = (kind: DateKind) => {
    const base: FuzzyDate = { kind, certainty: kind === 'exact' || kind === 'month' || kind === 'year' ? 'confirmed' : 'approximate' };
    if (kind === 'unknown') return onChange({ kind: 'unknown' });
    if (kind === 'decade') {
      const y = parts.year ?? 1950;
      const dec = Math.floor(y / 10) * 10;
      return onChange({ ...base, start: String(dec), end: String(dec + 9) });
    }
    if (kind === 'range') return onChange({ ...base, start: String(parts.year ?? thisYear - 50), end: String(parts.year ?? thisYear - 40) });
    if (kind === 'relative') return onChange({ ...base, relativePersonId: value.relativePersonId ?? people[0]?.id, relativeAge: value.relativeAge ?? 12, displayText: value.displayText });
    if (kind === 'before_after') return onChange({ ...base, beforeAfter: value.beforeAfter ?? 'before', relativeEventId: value.relativeEventId ?? events[0]?.id, displayText: value.displayText });
    if (defaultToToday) {
      const today = new Date();
      const y = String(today.getFullYear());
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      const keep = parts.year ? String(parts.year) : null;
      if (kind === 'exact') return onChange({ ...base, start: value.start && /^\d{4}-\d{2}-\d{2}$/.test(value.start) ? value.start : `${y}-${m}-${d}` });
      if (kind === 'month') return onChange({ ...base, start: parts.year && parts.month ? `${parts.year}-${parts.month}` : `${keep ?? y}-${m}` });
      if (kind === 'year') return onChange({ ...base, start: keep ?? y });
    }
    return onChange({ ...base, start: value.start ?? String(thisYear - 30) });
  };

  const setYear = (y: string, which: 'start' | 'end' = 'start') => {
    const n = y.replace(/\D/g, '').slice(0, 4);
    if (which === 'end') return onChange({ ...value, end: n });
    if (value.kind === 'exact') return onChange({ ...value, start: build(n, parts.month, parts.day) });
    if (value.kind === 'month') return onChange({ ...value, start: build(n, parts.month) });
    if (value.kind === 'decade') {
      const dec = Math.floor((Number(n) || 0) / 10) * 10;
      return onChange({ ...value, start: String(dec), end: String(dec + 9) });
    }
    return onChange({ ...value, start: n });
  };

  const preview = formatFuzzyDate(value, lang);

  return (
    <div className="stack">
      <div className="field">
        <label className="field-label" htmlFor={`${id}-kind`}>
          {label ?? t('date.kind')}
        </label>
        <select id={`${id}-kind`} className="input" value={value.kind} onChange={(e) => setKind(e.target.value as DateKind)}>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {t(`date.kind.${k}` as 'date.kind.exact')}
            </option>
          ))}
        </select>
      </div>

      {value.kind === 'exact' && (
        <div className="field">
          <label className="field-label" htmlFor={`${id}-date`}>
            {t('date.day')}
          </label>
          <input
            id={`${id}-date`}
            className="input"
            type="date"
            value={value.start && /^\d{4}-\d{2}-\d{2}$/.test(value.start) ? value.start : ''}
            min="1850-01-01"
            max={`${thisYear}-12-31`}
            onChange={(e) => onChange({ ...value, start: e.target.value, certainty: 'confirmed' })}
          />
        </div>
      )}

      {value.kind === 'month' && (
        <div className="input-row">
          <div className="field grow">
            <label className="field-label" htmlFor={`${id}-month`}>
              {t('date.month')}
            </label>
            <select
              id={`${id}-month`}
              className="input"
              value={parts.month ?? ''}
              onChange={(e) => onChange({ ...value, start: build(String(parts.year ?? thisYear), e.target.value) })}
            >
              <option value="">—</option>
              {monthNames[lang].map((m, i) => (
                <option key={m} value={String(i + 1).padStart(2, '0')}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ width: 120 }}>
            <label className="field-label" htmlFor={`${id}-my`}>
              {t('date.year')}
            </label>
            <YearSelect id={`${id}-my`} value={parts.year ? String(parts.year) : ''} onChange={(y) => setYear(y)} />
          </div>
        </div>
      )}

      {(value.kind === 'year' || value.kind === 'about') && (
        <div className="field">
          <label className="field-label" htmlFor={`${id}-y`}>
            {t('date.year')}
          </label>
          <YearSelect id={`${id}-y`} value={parts.year ? String(parts.year) : ''} onChange={(y) => setYear(y)} />
        </div>
      )}

      {value.kind === 'decade' && (
        <div className="field">
          <label className="field-label" htmlFor={`${id}-dec`}>
            {t('date.decade')}
          </label>
          <select id={`${id}-dec`} className="input" value={value.start ?? ''} onChange={(e) => setYear(e.target.value)}>
            {decades(thisYear).map((d) => (
              <option key={d} value={String(d)}>
                {lang === 'he' ? `שנות ה-${d}` : `${d}s`}
              </option>
            ))}
          </select>
        </div>
      )}

      {value.kind === 'range' && (
        <div className="input-row">
          <div className="field grow">
            <label className="field-label" htmlFor={`${id}-from`}>
              {t('date.fromYear')}
            </label>
            <YearSelect id={`${id}-from`} value={parts.year ? String(parts.year) : ''} onChange={(y) => setYear(y)} />
          </div>
          <div className="field grow">
            <label className="field-label" htmlFor={`${id}-to`}>
              {t('date.toYear')}
            </label>
            <YearSelect id={`${id}-to`} value={endParts.year ? String(endParts.year) : ''} onChange={(y) => setYear(y, 'end')} />
          </div>
        </div>
      )}

      {value.kind === 'relative' && (
        <>
          <div className="input-row">
            <div className="field grow">
              <label className="field-label" htmlFor={`${id}-rp`}>
                {t('date.relativePerson')}
              </label>
              <select id={`${id}-rp`} className="input" value={value.relativePersonId ?? ''} onChange={(e) => onChange({ ...value, relativePersonId: e.target.value })}>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {displayName(p, lang)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 110 }}>
              <label className="field-label" htmlFor={`${id}-ra`}>
                {t('date.relativeAge')}
              </label>
              <select
                id={`${id}-ra`}
                className="input"
                value={value.relativeAge ?? ''}
                onChange={(e) => onChange({ ...value, relativeAge: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">—</option>
                {AGES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <FreeText value={value} onChange={onChange} />
        </>
      )}

      {value.kind === 'before_after' && (
        <>
          <div className="input-row">
            <div className="field" style={{ width: 140 }}>
              <label className="field-label" htmlFor={`${id}-ba`}>
                {t('date.beforeAfter')}
              </label>
              <select id={`${id}-ba`} className="input" value={value.beforeAfter ?? 'before'} onChange={(e) => onChange({ ...value, beforeAfter: e.target.value as 'before' | 'after' })}>
                <option value="before">{t('date.before')}</option>
                <option value="after">{t('date.after')}</option>
              </select>
            </div>
            <div className="field grow">
              <label className="field-label" htmlFor={`${id}-ev`}>
                {t('date.event')}
              </label>
              <select
                id={`${id}-ev`}
                className="input"
                value={value.relativeEventId ?? ''}
                onChange={(e) => {
                  const ev = events.find((x) => x.id === e.target.value);
                  onChange({ ...value, relativeEventId: e.target.value, displayText: ev ? (lang === 'he' ? ev.titleHe ?? ev.title : ev.title) : value.displayText });
                }}
              >
                <option value="">—</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {lang === 'he' ? ev.titleHe ?? ev.title : ev.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <FreeText value={value} onChange={onChange} />
        </>
      )}

      {!compact && value.kind !== 'unknown' && (
        <p className="subtle-hint" aria-live="polite">
          {t('date.preview', { text: preview })}
        </p>
      )}
    </div>
  );
}

/**
 * Years, newest first, back to 1850 — and ages 0 to 120.
 *
 * Both used to be typed in, and a typed year is where the mistakes live: a
 * missing digit, 9151 for 1951, a year that has not happened yet. A list can
 * only give a real answer, and on a phone it is a wheel instead of a keyboard.
 */
const YEARS: number[] = (() => {
  const out: number[] = [];
  for (let y = new Date().getFullYear(); y >= 1850; y--) out.push(y);
  return out;
})();
const AGES: number[] = Array.from({ length: 121 }, (_, i) => i);

/**
 * A year, chosen rather than typed. A year already stored outside the list —
 * an ancestor born in 1804, say — is kept and offered, so opening an old
 * record can never quietly change it.
 */
function YearSelect({ id, value, onChange }: { id: string; value: string; onChange: (year: string) => void }) {
  const n = Number(value);
  const years = value && Number.isFinite(n) && !YEARS.includes(n) ? [n, ...YEARS] : YEARS;
  return (
    <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {years.map((y) => (
        <option key={y} value={String(y)}>
          {y}
        </option>
      ))}
    </select>
  );
}

function FreeText({ value, onChange }: { value: FuzzyDate; onChange: (d: FuzzyDate) => void }) {
  const { t } = useI18n();
  const id = useId();
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {t('date.freeText')}
      </label>
      <input id={id} className="input" value={value.displayText ?? ''} placeholder={t('date.freeTextPlaceholder')} onChange={(e) => onChange({ ...value, displayText: e.target.value })} />
    </div>
  );
}

function parseParts(s?: string): { year?: number; month?: string; day?: string } {
  if (!s) return {};
  const m = /^(\d{1,4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(s);
  if (!m) return {};
  return { year: Number(m[1]), month: m[2], day: m[3] };
}

function build(year: string, month?: string, day?: string): string {
  if (!year) return '';
  if (month && day) return `${year.padStart(4, '0')}-${month}-${day}`;
  if (month) return `${year.padStart(4, '0')}-${month}`;
  return year;
}

function decades(thisYear: number): number[] {
  const out: number[] = [];
  for (let d = Math.floor(thisYear / 10) * 10; d >= 1850; d -= 10) out.push(d);
  return out;
}

/** Read-only date label with an "approximate" hint where relevant. */
export function FuzzyDateLabel({ date, showApprox = true }: { date?: FuzzyDate; showApprox?: boolean }) {
  const { t, lang } = useI18n();
  if (!date) return null;
  const text = formatFuzzyDate(date, lang);
  const approx = date.kind !== 'exact' && date.kind !== 'month' && date.kind !== 'unknown';
  return (
    <span>
      {text}
      {showApprox && approx ? <span className="faint xs"> · {t('story.approximate')}</span> : null}
    </span>
  );
}
